import { useState } from 'react';

import { useTrackPageView } from 'modules/analytics';
import { useIsEditor } from 'modules/archive';
import { AuthShowContainer, AuthorizedContent } from 'modules/auth';
import { getCurrentUser } from 'modules/data';
import { Features } from 'modules/features';
import { HelpText } from 'modules/help-text';
import { useI18n } from 'modules/i18n';
import { ErrorBoundary } from 'modules/react-toolbox';
import { Modal } from 'modules/ui';
import { Helmet } from 'react-helmet';
import { FaPencilAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';

import AfterUpdateEmailPopup from './AfterUpdateEmailPopup';
import PasskeyPopup from './PasskeyPopup';
import PendingEmailChange from './PendingEmailChange';
import TwoFAPopup from './TwoFAPopup';
import UserDetailsContainer from './UserDetailsContainer';
import UserDetailsForm from './UserDetailsForm';
import UserProjects from './UserProjects';

export default function AccountPage() {
    const { t } = useI18n();
    const isEditor = useIsEditor();
    const user = useSelector(getCurrentUser);
    const [showEmailChangePopup, setShowEmailChangePopup] = useState(false);
    useTrackPageView();

    return (
        <div className="wrapper-content register account-page">
            <Helmet>
                <title>{t('account_page')}</title>
            </Helmet>
            <ErrorBoundary>
                <AuthShowContainer hasProjectAccess ifNoProject>
                    {isEditor && <HelpText code="account_page" />}

                    <div className="account-page-header">
                        <h1 className="Page-main-title">{t('account_page')}</h1>

                        <div className="account-actions">
                            <div className="account-actions-icon edit-account-icon">
                                <Modal
                                    title={t('edit.user.edit')}
                                    trigger={
                                        <FaPencilAlt className="Icon Icon--primary" />
                                    }
                                >
                                    {(close) => (
                                        <UserDetailsForm
                                            onSubmit={close}
                                            onCancel={close}
                                            onEmailChange={() =>
                                                setShowEmailChangePopup(true)
                                            }
                                        />
                                    )}
                                </Modal>
                            </div>
                            {user?.otp_required_for_login && (
                                <div className="account-actions-icon two-fa-icon">
                                    <TwoFAPopup showDialogInitially={false} />
                                </div>
                            )}
                            {user?.passkey_required_for_login && (
                                <div className="account-actions-icon passkey-icon">
                                    <PasskeyPopup showDialogInitially={false} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="user-registration boxes">
                        {user && <UserDetailsContainer />}
                    </div>
                    {user?.unconfirmed_email && <PendingEmailChange />}
                    {showEmailChangePopup && (
                        <AfterUpdateEmailPopup
                            onClose={() => setShowEmailChangePopup(false)}
                        />
                    )}
                    <div className="user-registration boxes">
                        {user && <UserProjects />}
                    </div>
                    <AuthorizedContent
                        object={{ type: 'General' }}
                        action="edit"
                    >
                        <Features />
                    </AuthorizedContent>
                </AuthShowContainer>
                <AuthShowContainer ifLoggedOut>
                    {t('devise.failure.unauthenticated')}
                </AuthShowContainer>
            </ErrorBoundary>
        </div>
    );
}
